import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ArrowInline } from "@/lib/Icon";

import { useBootstrap } from "@/lib/api/hooks";
import { adminOverviewQueryOptions } from "@/lib/api/adminHooks";
import { SONNY_CHAT_ENABLED } from "@/features/admin/sonnyConfig";

/**
 * Admin shell. Grew out of a top-bar nav; converted to a left SIDEBAR
 * (2026-08-04) because the item count outgrew one row — a vertical list
 * stacks indefinitely. Desktop: fixed sidebar with grouped links, user +
 * "View as patient" pinned at the bottom. Mobile: slim top bar with a
 * hamburger that opens the same nav as an overlay drawer.
 *
 * Admin routes remain a distinct surface (internal analytics + ops), not
 * a variant of the patient UI; "View as patient" is the escape hatch back
 * to `/app`.
 */

// `children` renders an indented Linear-style submenu, auto-expanded while the
// parent's path is active (prefix match).
const NAV_GROUPS: {
  label: string;
  items: {
    to: string;
    title: string;
    fullAdminOnly?: boolean;
    end?: boolean;
    children?: { to: string; title: string; end?: boolean }[];
  }[];
}[] = [
  {
    label: "Analytics",
    items: [
      { to: "/admin", title: "Overview" },
      { to: "/admin/subscriptions", title: "Subscriptions" },
      { to: "/admin/cohorts", title: "Cohort retention" },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        to: "/admin/clinical-admin",
        title: "Clinical Admin",
        children: [
          { to: "/admin/clinical-admin", title: "Overview", end: true },
          { to: "/admin/clinical-admin/reporting", title: "Reporting" },
        ],
      },
      {
        to: "/admin/support",
        title: "Support",
        children: [
          { to: "/admin/support/metrics", title: "Metrics" },
          { to: "/admin/support", title: "Issues", end: true },
          { to: "/admin/support/views", title: "Views" },
          { to: "/admin/support/settings", title: "Settings" },
        ],
      },
      { to: "/admin/referrals", title: "Referrals" },
      { to: "/admin/lifecycle-emails", title: "Lifecycle" },
      {
        to: "/admin/storefronts",
        title: "Storefronts",
        fullAdminOnly: true,
        children: [
          { to: "/admin/storefronts", title: "Stores", end: true },
          { to: "/admin/storefronts/invites", title: "Owner invites" },
        ],
      },
    ],
  },
  {
    label: "System",
    items: [
      {
        to: "/admin/system-ops",
        title: "System Operations",
        fullAdminOnly: true,
      },
      { to: "/admin/feature-flags", title: "Feature Flags" },
      { to: "/admin/admins", title: "Admins" },
      { to: "/admin/patient-profiles", title: "Create profile" },
    ],
  },
  {
    label: "AI",
    items: [
      ...(SONNY_CHAT_ENABLED
        ? [
            {
              to: "/admin/sonny",
              title: "Chat",
              fullAdminOnly: true,
              end: true,
            },
          ]
        : []),
    ],
  },
];

export function AdminLayout() {
  const { data } = useBootstrap();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSonnyChat = location.pathname === "/admin/sonny";

  // Warm Overview while an admin works elsewhere. The server snapshot is
  // shared, so this is normally a sub-second read and avoids a navigation-time
  // loading state later.
  useEffect(() => {
    if (data?.is_admin && location.pathname !== "/admin") {
      void queryClient.prefetchQuery(adminOverviewQueryOptions());
    }
  }, [data?.is_admin, location.pathname, queryClient]);

  // Non-admins shouldn't land here, but if the RequireAdmin guard hasn't run
  // yet (first render of a fresh session), render nothing rather than a
  // half-drawn admin UI that'll flash and then redirect.
  if (!data?.is_admin) return null;

  return (
    <div className="min-h-screen bg-page text-ink">
      {/* Mobile top bar (hamburger) */}
      <header className="md:hidden sticky top-0 z-50 border-b border-line bg-raised/80 backdrop-blur">
        <div className="px-15 py-12 flex items-center justify-between">
          <Brand />
          <button
            type="button"
            aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setDrawerOpen((v) => !v)}
            className="p-6 rounded border border-line hover:bg-raised"
          >
            {drawerOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-[260px] bg-page border-r border-line pt-[64px] flex flex-col">
            <SidebarNav
              adminRole={data.admin_role}
              onNavigate={() => setDrawerOpen(false)}
            />
            <SidebarFooter
              email={data.patient.email}
              onViewAsPatient={() => navigate("/app")}
            />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-40 w-[230px] flex-col border-r border-line bg-raised/40">
        <div className="px-20 py-20 border-b border-line">
          <Brand />
        </div>
        <SidebarNav adminRole={data.admin_role} />
        <SidebarFooter
          email={data.patient.email}
          onViewAsPatient={() => navigate("/app")}
        />
      </aside>

      <main className="md:pl-[230px]">
        <div
          className={
            isSonnyChat
              ? "h-[calc(100vh-53px)] md:h-screen"
              : "max-w-content mx-auto px-15 md:px-40 py-25 md:py-40"
          }
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-10">
      <span className="type-label-md font-semibold">System Admin</span>
      <span className="text-10 uppercase tracking-wider text-ink-muted border border-line px-6 py-2 rounded">
        internal
      </span>
    </div>
  );
}

function SidebarNav({
  adminRole,
  onNavigate,
}: {
  adminRole: string | null;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const isFullAdmin = adminRole === "admin" || adminRole === null;
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.fullAdminOnly || isFullAdmin),
  })).filter((group) => group.items.length > 0);
  return (
    <nav className="flex-1 overflow-y-auto px-10 py-16 flex flex-col gap-20">
      {visibleGroups.map((group) => (
        <div key={group.label} className="flex flex-col gap-4">
          <span className="px-10 text-10 uppercase tracking-wider text-ink-muted">
            {group.label}
          </span>
          {group.items.map((item) => {
            const expanded =
              !!item.children && location.pathname.startsWith(item.to);
            return (
              <div key={item.to} className="flex flex-col gap-2">
                <AdminNavLink to={item.to} onClick={onNavigate} end={item.end}>
                  {item.title}
                </AdminNavLink>
                {expanded && (
                  <div className="ml-10 pl-8 border-l border-line flex flex-col gap-2">
                    {item.children!.map((c) => (
                      <AdminNavLink
                        key={c.to}
                        to={c.to}
                        onClick={onNavigate}
                        end={c.end}
                      >
                        {c.title}
                      </AdminNavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function SidebarFooter({
  email,
  onViewAsPatient,
}: {
  email: string;
  onViewAsPatient: () => void;
}) {
  return (
    <div className="border-t border-line px-15 py-15 flex flex-col gap-10">
      <span className="type-label-sm text-ink-muted truncate" title={email}>
        {email}
      </span>
      <button
        type="button"
        onClick={onViewAsPatient}
        className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised text-left"
      >
        View as patient <ArrowInline />
      </button>
    </div>
  );
}

function AdminNavLink({
  to,
  children,
  onClick,
  end,
}: {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end ?? to === "/admin"}
      onClick={onClick}
      className={({ isActive }) =>
        `px-10 py-8 rounded type-label-sm ${
          isActive
            ? "bg-raised text-ink font-medium"
            : "text-ink-muted hover:text-ink hover:bg-raised/60"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
